import {
  lazy,
  Suspense,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useQueuedPopup } from "@/hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { useUserDataFlattened } from "@/modules/user/stores";
import type { VipUpDialogResult } from "./useVipUpDialog.types";

const VipUpDialog = lazy(() =>
  import("../components/VipUpDialog/VipUpDialog").then(m => ({
    default: m.VipUpDialog,
  }))
);

export type UpgradeStep = { prev: number; current: number };

/** 生成 fromLevel → toLevel 之间每一级的升级步骤（如 0→2 为 [0→1, 1→2]） */
export function buildUpgradeSteps(
  fromLevel: number,
  toLevel: number
): UpgradeStep[] {
  const steps: UpgradeStep[] = [];
  for (let level = fromLevel; level < toLevel; level++) {
    steps.push({ prev: level, current: level + 1 });
  }
  return steps;
}

/**
 * 根据 API 当前等级与已排队等级，推断本次应追平到的目标等级。
 * 后端可能分多次推送（如先 level=0 再 level=1），也可能一次跳过数级。
 */
export function resolveUpgradeTargetLevel(
  apiLevel: number,
  lastQueuedTarget: number,
  /** 本次是否为 upgraded 从 false→true 的首次信号（API 等级可能尚未 +1） */
  isFirstUpgradedSignal: boolean
): number {
  if (apiLevel > lastQueuedTarget) {
    return apiLevel;
  }
  if (apiLevel === lastQueuedTarget) {
    // 仅首次 upgraded 且等级与队列持平：认为 API 还没 +1，补排一级
    // 已展示过该级（如 0→1 关窗后 API 已是 1）则不再 +1，避免弹窗关不掉
    return isFirstUpgradedSignal ? apiLevel + 1 : lastQueuedTarget;
  }
  return lastQueuedTarget;
}

export const useVipUpDialog = (): VipUpDialogResult => {
  const { data: userInfo } = useUserDataFlattened();
  /** 控制弹窗队列入队，从首次升级触发直到所有步骤展示完毕 */
  const [shouldShowPopup, setShouldShowPopup] = useState(false);
  const [activeUpgrade, setActiveUpgrade] = useState<UpgradeStep | null>(null);

  const prevLevelRef = useRef<number>(0);
  const prevUpgradedRef = useRef<boolean>(false);
  const lastQueuedTargetRef = useRef<number>(0);
  const upgradeQueueRef = useRef<UpgradeStep[]>([]);
  const isShowingRef = useRef(false);

  const syncPendingPopupState = useCallback(() => {
    const hasPending =
      isShowingRef.current || upgradeQueueRef.current.length > 0;
    setShouldShowPopup(hasPending);
  }, []);

  const processNextInQueue = useCallback(() => {
    if (isShowingRef.current) return;
    const next = upgradeQueueRef.current.shift();
    if (!next) {
      syncPendingPopupState();
      return;
    }
    isShowingRef.current = true;
    setActiveUpgrade(next);
    setShouldShowPopup(true);
  }, [syncPendingPopupState]);

  // upgraded=false 时同步记录当前等级，供下次升级使用
  useEffect(() => {
    if (userInfo?.upgraded) return;

    const level = userInfo?.vipLevel?.level ?? 0;
    prevLevelRef.current = level;
    prevUpgradedRef.current = false;
    lastQueuedTargetRef.current = level;

    // 升级周期结束且没有进行中的弹窗时，才清理队列
    if (!isShowingRef.current && upgradeQueueRef.current.length === 0) {
      setActiveUpgrade(null);
      setShouldShowPopup(false);
    }
  }, [userInfo?.vipLevel?.level, userInfo?.upgraded]);

  // upgraded=true 时：根据 API 等级变化追加升级步骤，依次弹窗
  useEffect(() => {
    const upgraded = userInfo?.upgraded ?? false;
    if (!upgraded) return;

    const apiLevel = userInfo?.vipLevel?.level ?? 0;

    if (!prevUpgradedRef.current) {
      lastQueuedTargetRef.current = prevLevelRef.current;
    }

    const isFirstUpgradedSignal = !prevUpgradedRef.current;
    const targetLevel = resolveUpgradeTargetLevel(
      apiLevel,
      lastQueuedTargetRef.current,
      isFirstUpgradedSignal
    );

    if (targetLevel > lastQueuedTargetRef.current) {
      const steps = buildUpgradeSteps(lastQueuedTargetRef.current, targetLevel);
      upgradeQueueRef.current.push(...steps);
      lastQueuedTargetRef.current = targetLevel;
      setShouldShowPopup(true);
      processNextInQueue();
    }

    prevUpgradedRef.current = true;
  }, [userInfo?.upgraded, userInfo?.vipLevel?.level, processNextInQueue]);

  const handleComplete = useCallback(() => {
    isShowingRef.current = false;

    if (upgradeQueueRef.current.length > 0) {
      setShouldShowPopup(true);
      processNextInQueue();
      return;
    }

    setActiveUpgrade(null);
    setShouldShowPopup(false);
  }, [processNextInQueue]);

  const VipUpDialogContent: React.FC<{
    onComplete: () => void;
    currentVipLevel: number;
    prevVipLevel: number;
  }> = ({ onComplete, currentVipLevel, prevVipLevel }) => {
    return (
      <Suspense fallback={null}>
        <VipUpDialog
          currentVipLevel={currentVipLevel}
          prevVipLevel={prevVipLevel}
          onComplete={() => {
            handleComplete();
            onComplete();
          }}
        />
      </Suspense>
    );
  };

  const displayLevel = activeUpgrade?.current ?? 0;
  const prevLevel = activeUpgrade?.prev ?? 0;

  const { trigger } = useQueuedPopup({
    popupId: POPUP_CONFIGS.VIP_UP.id,
    priority: POPUP_CONFIGS.VIP_UP.priority,
    shouldShow: () => shouldShowPopup,
    renderContent: onComplete => (
      <VipUpDialogContent
        onComplete={onComplete}
        currentVipLevel={displayLevel}
        prevVipLevel={prevLevel}
      />
    ),
    showOnce: false,
    allowManualTrigger: false,
    dependencies: [shouldShowPopup, displayLevel, prevLevel],
  });

  return { trigger };
};
